<template>
    <div>
        <v-card>
            <v-card-title class="headline">
                <v-btn icon @click="closeDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                <span>Edit Invoice</span>
            </v-card-title>
            <v-card-text>
                <v-form v-model="valid">
                    <v-row v-if="canEditInvoice">
                        <v-col cols="12" md="6">
                            <v-menu
                                v-model="issueDateMenu"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                                :z-index="1000"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        :value="issueDateText"
                                        label="Issue Date"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    :value="issueDateText"
                                    @input="onIssueDatePickerInput"
                                    scrollable
                                />
                            </v-menu>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-menu
                                v-model="dateMenu"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                                :z-index="1000"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        :value="dueDateText"
                                        label="Due Date"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    :value="dueDateText"
                                    @input="onDueDatePickerInput"
                                    scrollable
                                />
                            </v-menu>
                        </v-col>
                    </v-row>
                    <v-row v-if="canEditInvoice">
                        <v-col cols="12">
                            <v-text-field
                                v-model="fileNameDraft"
                                label="File name"
                                prepend-icon="mdi-file-document-outline"
                                :disabled="!!selectedFile"
                            />
                        </v-col>
                    </v-row>
                    <v-row v-if="canMarkCreditNote">
                        <v-col cols="12">
                            <v-switch
                                :input-value="!!localInvoice.creditNote"
                                label="Credit note"
                                color="error"
                                hide-details
                                class="mt-0"
                                @change="onCreditNoteChange"
                            />
                            <p
                                v-if="localInvoice.creditNote"
                                class="mb-0 mt-2"
                                style="color: #f44336; font-weight: 600;"
                            >
                                {{ localInvoice.consecutive }}
                            </p>
                        </v-col>
                    </v-row>
                    <v-row class="my-n2">
                        <v-col cols="6" v-if="localInvoice.fileName">
                            <div class="d-flex align-center">
                                <span class="text-body-2 mr-2"
                                    >Current invoice:</span
                                >
                                <a
                                    href="#"
                                    @click.prevent="
                                        openCurrentInvoice(
                                            localInvoice.fileName
                                        )
                                    "
                                    class="text-body-2 text-decoration-none"
                                    style="color: #1976d2; cursor: pointer;"
                                >
                                    {{
                                        localInvoice.consecutive ||
                                            localInvoice.fileName
                                    }}
                                </a>
                            </div>
                        </v-col>
                        <v-col cols="6" v-if="selectedFile">
                            <div class="d-flex align-center">
                                <span class="text-body-2 mr-2"
                                    >New invoice:</span
                                >
                                <a
                                    :href="fileUrl"
                                    target="_blank"
                                    class="text-body-2 text-decoration-none"
                                    style="color: #1976d2;"
                                >
                                    {{ selectedFile.name }}
                                </a>
                                <v-btn
                                    icon
                                    x-small
                                    color="error"
                                    class="mx-1"
                                    @click="removeFile"
                                >
                                    <v-icon x-small>mdi-close-circle</v-icon>
                                </v-btn>
                            </div>
                        </v-col>
                    </v-row>
                    <v-row v-if="canRejectInvoice">
                        <v-col cols="12">
                            <v-btn
                                @click="$refs.fileInput.click()"
                                color="primary"
                                outlined
                                prepend-icon="mdi-file-upload"
                                block
                            >
                                New Invoice File
                            </v-btn>
                            <input
                                ref="fileInput"
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                @change="handleFileSelection"
                                style="display: none"
                            />
                        </v-col>
                    </v-row>
                    <v-row
                        v-if="
                            localInvoice.pastFiles &&
                                localInvoice.pastFiles.length > 0
                        "
                    >
                        <v-col cols="12">
                            <div class="text-body-2 mb-2">
                                Past invoices:
                            </div>
                            <div
                                v-for="(file, index) in localInvoice.pastFiles"
                                :key="index"
                                class="d-flex align-center mb-2"
                            >
                                <a
                                    href="#"
                                    @click.prevent="openCurrentInvoice(file)"
                                    class="text-body-2 text-decoration-none"
                                    style="color: #1976d2; cursor: pointer;"
                                >
                                    {{ file }}
                                </a>
                            </div>
                        </v-col>
                    </v-row>

                    <!-- Invoice Attachments Section -->
                    <v-divider class="my-4"></v-divider>
                    <v-row>
                        <v-col cols="12">
                            <div class="text-body-1 font-weight-medium mb-2">
                                Invoice Attachments
                            </div>
                        </v-col>
                    </v-row>
                    <v-row v-if="invoiceAttachments.length > 0">
                        <v-col cols="12">
                            <div class="text-body-2 mb-2">
                                Current attachments:
                            </div>
                            <div
                                v-for="(attachment,
                                index) in invoiceAttachments"
                                :key="index"
                                class="d-flex align-center mb-2"
                            >
                                <v-text-field
                                    v-if="
                                        canEditInvoice &&
                                            attachmentOrigins[index]
                                    "
                                    v-model="invoiceAttachments[index]"
                                    dense
                                    hide-details
                                    class="mr-2"
                                    style="max-width: 360px;"
                                />
                                <a
                                    v-else
                                    href="#"
                                    @click.prevent="
                                        openAttachment(
                                            attachmentOrigins[index] ||
                                                attachment
                                        )
                                    "
                                    class="text-body-2 text-decoration-none"
                                    style="color: #1976d2; cursor: pointer;"
                                >
                                    {{ attachment }}
                                </a>
                                <v-btn
                                    v-if="canRejectInvoice"
                                    icon
                                    x-small
                                    color="error"
                                    class="ml-2"
                                    @click="confirmDeleteAttachment(attachment)"
                                    :loading="deletingAttachment === attachment"
                                >
                                    <v-icon x-small>mdi-delete</v-icon>
                                </v-btn>
                            </div>
                        </v-col>
                    </v-row>
                    <v-row v-if="canRejectInvoice">
                        <v-col cols="12">
                            <v-btn
                                @click="$refs.attachmentInput.click()"
                                color="primary"
                                outlined
                                prepend-icon="mdi-paperclip"
                                block
                                :loading="addingAttachment"
                            >
                                Attach
                            </v-btn>
                            <input
                                ref="attachmentInput"
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                multiple
                                @change="handleAttachmentSelection"
                                style="display: none"
                            />
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>

            <v-card-actions class="mt-3">
                <v-btn
                    v-if="canRejectInvoice && !localInvoice.rejected"
                    text
                    color="error"
                    @click="rejectDialog = true"
                    :disabled="loading"
                >
                    Reject
                </v-btn>
                <v-btn
                    v-if="canRejectInvoice && localInvoice.rejected"
                    text
                    color="error"
                    @click="cancelRejectionDialog = true"
                    :disabled="loading"
                >
                    Cancel Rejection
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                    text
                    color="primary"
                    @click="updatePurchaseInvoice"
                    :loading="loading"
                    :disabled="!canSave"
                >
                    Save
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- Reject Dialog -->
        <v-dialog v-model="rejectDialog" max-width="400px" persistent>
            <v-card>
                <v-card-title>
                    Reject Invoice
                </v-card-title>
                <v-card-text>
                    <v-alert
                        v-if="rejectingInvoiceHasSap"
                        type="warning"
                        text
                        dense
                        class="mb-3"
                    >
                        This invoice already has SAP checked. Rejecting it keeps
                        that mark. The invoice stays in the process as rejected
                        and is left out of accounting calculations and
                        validations.
                    </v-alert>
                    <p>Are you sure you want to reject this invoice?</p>
                </v-card-text>
                <v-card-actions>
                    <v-btn text color="secondary" @click="rejectDialog = false">
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="error" @click="reject" :disabled="loading">
                        Reject
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Cancel Rejection Dialog -->
        <v-dialog v-model="cancelRejectionDialog" max-width="400px" persistent>
            <v-card>
                <v-card-title>
                    Cancel Rejection
                </v-card-title>
                <v-card-text>
                    <p>
                        Are you sure you want to cancel the rejection of this
                        invoice?
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        text
                        color="secondary"
                        @click="cancelRejectionDialog = false"
                    >
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="error"
                        @click="cancelRejection"
                        :disabled="loading"
                    >
                        Cancel
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete Attachment Confirmation Dialog -->
        <v-dialog v-model="deleteAttachmentDialog" max-width="400px" persistent>
            <v-card>
                <v-card-title>
                    Delete Attachment
                </v-card-title>
                <v-card-text>
                    <p>
                        Are you sure you want to delete the attachment
                        <strong>{{ attachmentToDelete }}</strong
                        >?
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        text
                        color="secondary"
                        @click="deleteAttachmentDialog = false"
                    >
                        Cancel
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="error"
                        @click="deleteAttachment"
                        :loading="deletingAttachment === attachmentToDelete"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import _ from 'lodash'
import API from '@/services/api'
import { mapMutations } from 'vuex'
import { saveFile } from '@/services/storage/saveFile'
import { deleteFile } from '@/services/storage/deleteFile'
import { openFile } from '@/services/storage/openFile'
import { renameFile } from '@/services/storage/renameFile'
import { withCreditNotePrefix } from '@/components/accountant/creditNote'

export default {
    name: 'EditInvoice',
    props: {
        invoice: {
            type: Object,
            required: true,
        },
        purchase: {
            type: Object,
            required: true,
        },
        canEditInvoice: {
            type: Boolean,
            default: false,
        },
        canRejectInvoice: {
            type: Boolean,
            default: false,
        },
        canMarkCreditNote: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            valid: false,
            loading: false,
            dateMenu: false,
            issueDateMenu: false,
            issueDateText: '',
            dueDateText: '',
            fileNameDraft: '',
            originalFileName: '',
            selectedFile: null,
            fileUrl: null,
            rejectDialog: false,
            localInvoice: { dueDate: '', consecutive: '' },
            companyId: JSON.parse(localStorage.getItem('company')),
            cancelRejectionDialog: false,
            // Invoice Attachments management
            invoiceAttachments: [],
            originalInvoiceAttachments: [],
            attachmentOrigins: [],
            deleteAttachmentDialog: false,
            attachmentToDelete: null,
            deletingAttachment: null,
            addingAttachment: false,
            attachmentsToAdd: [],
            attachmentsToRemove: [],
            pendingAttachmentsToUpload: [],
        }
    },
    mounted() {
        this.localInvoice = _.cloneDeep(this.invoice)
        const issueDate = this.toPickerDate(
            this.localInvoice.invoicesDate || this.localInvoice.invoiceDate
        )
        this.issueDateText = issueDate
        const pickerDate = this.toPickerDate(this.localInvoice.dueDate)
        this.dueDateText = pickerDate
        this.$set(this.localInvoice, 'dueDate', pickerDate)
        this.originalFileName = this.localInvoice.fileName || ''
        this.fileNameDraft = this.originalFileName
        this.loadInvoiceAttachments()
    },
    computed: {
        canSave() {
            if (this.loading) return false
            if (this.canEditInvoice) return true
            if (this.canMarkCreditNote) return true
            return !!(
                this.canRejectInvoice &&
                (this.selectedFile ||
                    this.attachmentsToAdd.length ||
                    this.attachmentsToRemove.length)
            )
        },
        rejectingInvoiceHasSap() {
            const relatedEntries = this.localInvoice?.relatedEntries || []
            if (!relatedEntries.length || !this.purchase?.items) {
                return false
            }
            return relatedEntries.every(relatedEntry => {
                let foundEntry = null
                this.purchase.items.forEach(item => {
                    const entry = (item.entries || []).find(
                        candidate => candidate.id === relatedEntry.entryId
                    )
                    if (entry) {
                        foundEntry = entry
                    }
                })
                return foundEntry && foundEntry.sap === true
            })
        },
    },
    beforeDestroy() {
        if (this.fileUrl) {
            URL.revokeObjectURL(this.fileUrl)
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        toPickerDate(value) {
            if (!value) return ''
            if (typeof value === 'string') {
                const match = value.match(/^(\d{4}-\d{2}-\d{2})/)
                return match ? match[1] : ''
            }
            const seconds = value.seconds || value._seconds
            if (seconds) {
                const date = new Date(seconds * 1000)
                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')
                return `${year}-${month}-${day}`
            }
            return ''
        },
        onDueDatePickerInput(value) {
            this.dueDateText = value || ''
            this.$set(this.localInvoice, 'dueDate', this.dueDateText)
            this.dateMenu = false
        },
        onIssueDatePickerInput(value) {
            this.issueDateText = value || ''
            this.$set(this.localInvoice, 'invoicesDate', this.issueDateText)
            this.$set(this.localInvoice, 'invoiceDate', this.issueDateText)
            this.issueDateMenu = false
        },
        onCreditNoteChange(enabled) {
            if (enabled) {
                const previous = this.localInvoice.consecutive || null
                this.$set(
                    this.localInvoice,
                    'creditNotePreviousConsecutive',
                    previous
                )
                const base =
                    (previous && String(previous).trim()) ||
                    this.originalFileName ||
                    ''
                this.$set(
                    this.localInvoice,
                    'consecutive',
                    withCreditNotePrefix(base)
                )
                this.$set(this.localInvoice, 'creditNote', true)
                return
            }
            const restored =
                this.localInvoice.creditNotePreviousConsecutive !== undefined
                    ? this.localInvoice.creditNotePreviousConsecutive
                    : null
            this.$set(this.localInvoice, 'consecutive', restored || null)
            this.$set(this.localInvoice, 'creditNote', false)
        },
        handleFileSelection(event) {
            const file = event.target.files[0]
            if (file) {
                if (this.fileUrl) {
                    URL.revokeObjectURL(this.fileUrl)
                }
                const usedNames = new Set(
                    this.collectUsedInvoiceFileNamesFromPurchase(this.purchase)
                )
                const newFileName = this.uniqueFileName(file.name, usedNames)
                const nextFile =
                    newFileName === file.name
                        ? file
                        : new File([file], newFileName, {
                              type: file.type,
                              lastModified: file.lastModified,
                          })
                this.selectedFile = nextFile
                this.fileUrl = URL.createObjectURL(nextFile)
            }
        },
        removeFile() {
            if (this.fileUrl) {
                URL.revokeObjectURL(this.fileUrl)
                this.fileUrl = null
            }
            this.selectedFile = null
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = ''
            }
        },
        async openCurrentInvoice(file) {
            const path = `${this.companyId}/purchases/${this.purchase.id}/invoices`
            await openFile(file, path)
        },
        async reject() {
            try {
                this.loading = true
                await API.rejectPurchaseInvoice({
                    companyId: this.companyId,
                    purchaseId: this.purchase.id,
                    fileName: this.localInvoice.fileName,
                    cancelRejection: false,
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeDialog()
            }
        },
        async cancelRejection() {
            try {
                this.loading = true
                await API.rejectPurchaseInvoice({
                    companyId: this.companyId,
                    purchaseId: this.purchase.id,
                    fileName: this.localInvoice.fileName,
                    cancelRejection: true,
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeDialog()
            }
        },
        closeDialog() {
            // Reset pending attachments when closing without saving
            this.pendingAttachmentsToUpload = []
            this.attachmentsToAdd = []
            this.attachmentsToRemove = []
            // Restore original attachments list
            this.invoiceAttachments = [...this.originalInvoiceAttachments]
            this.$emit('closeDialog')
        },
        loadInvoiceAttachments() {
            if (!this.localInvoice) {
                this.invoiceAttachments = []
                this.originalInvoiceAttachments = []
                this.attachmentOrigins = []
                return
            }

            const fromInvoice = this.localInvoice.invoiceAttachments || []
            const fromAttachmentsKey = this.localInvoice.attachments || []
            const merged = [...new Set([...fromInvoice, ...fromAttachmentsKey])]

            this.localInvoice.invoiceAttachments = merged
            this.invoiceAttachments = [...merged]
            this.originalInvoiceAttachments = [...merged]
            this.attachmentOrigins = merged.map(name => name)
        },
        async openAttachment(fileName) {
            const path = `${this.companyId}/purchases/${this.purchase.id}/invoiceAttatchments`
            await openFile(fileName, path)
        },
        handleAttachmentSelection(event) {
            const files = Array.from(event.target.files)
            if (files.length > 0) {
                this.addAttachments(files)
            }
            if (this.$refs.attachmentInput) {
                this.$refs.attachmentInput.value = ''
            }
        },
        parseInvoicesArray(raw) {
            if (raw == null || raw === '') return []
            if (Array.isArray(raw)) return raw
            if (typeof raw === 'string') {
                try {
                    const parsed = JSON.parse(raw)
                    return Array.isArray(parsed) ? parsed : []
                } catch {
                    return []
                }
            }
            return []
        },
        /** Invoice PDF names in the purchase folder (current + history). */
        collectUsedInvoiceFileNamesFromPurchase(purchase) {
            const names = new Set()
            const addInvoiceEntry = inv => {
                if (inv == null) return
                if (typeof inv === 'string') {
                    if (inv) names.add(inv)
                    return
                }
                const fn = inv.fileName || inv.filename || inv.nameFile
                if (fn) names.add(fn)
                if (Array.isArray(inv.pastFiles)) {
                    inv.pastFiles.forEach(f => names.add(f))
                }
            }

            const normalized = purchase?.purchaseInvoices || []
            const fromRaw = this.parseInvoicesArray(purchase?.invoices)
            const mergedInvoices = [...normalized, ...fromRaw]
            mergedInvoices.forEach(addInvoiceEntry)

            if (Array.isArray(purchase?.allDetailedInvoices)) {
                purchase.allDetailedInvoices.forEach(addInvoiceEntry)
            }

            return names
        },
        /** Collects attachment names used anywhere in the purchase (shared folder). */
        collectUsedAttachmentNamesFromPurchase(purchase) {
            const names = new Set()
            const normalized = purchase?.purchaseInvoices || []
            const fromRaw = this.parseInvoicesArray(purchase?.invoices)
            const list = [...normalized, ...fromRaw]
            for (const inv of list) {
                if (!inv || typeof inv === 'string') continue
                const arr = inv.invoiceAttachments || inv.attachments
                if (Array.isArray(arr)) arr.forEach(n => names.add(n))
            }
            return names
        },
        async addAttachments(files) {
            try {
                this.addingAttachment = true

                // Ensure we have the current list of attachments
                if (this.invoiceAttachments.length === 0) {
                    this.loadInvoiceAttachments()
                }

                const usedAttachmentNames = new Set([
                    ...this.collectUsedAttachmentNamesFromPurchase(
                        this.purchase
                    ),
                    ...this.invoiceAttachments,
                    ...this.pendingAttachmentsToUpload.map(f => f.name),
                ])
                const renamedFiles = files.map(file => {
                    const newFileName = this.uniqueFileName(
                        file.name,
                        usedAttachmentNames
                    )
                    return new File([file], newFileName, {
                        type: file.type,
                        lastModified: file.lastModified,
                    })
                })

                const newFileNames = renamedFiles.map(file => file.name)

                // Store renamed File objects temporarily (don't upload until Save)
                this.pendingAttachmentsToUpload = [
                    ...this.pendingAttachmentsToUpload,
                    ...renamedFiles,
                ]

                // Update local list for display
                this.invoiceAttachments = [
                    ...this.invoiceAttachments,
                    ...newFileNames,
                ]
                this.attachmentOrigins = [
                    ...this.attachmentOrigins,
                    ...newFileNames.map(() => null),
                ]

                // Add to attachmentsToAdd array for later confirmation
                this.attachmentsToAdd = [
                    ...this.attachmentsToAdd,
                    ...newFileNames,
                ]
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.addingAttachment = false
            }
        },
        confirmDeleteAttachment(fileName) {
            this.attachmentToDelete = fileName
            this.deleteAttachmentDialog = true
        },
        async deleteAttachment() {
            if (!this.attachmentToDelete) return

            try {
                this.deletingAttachment = this.attachmentToDelete

                const attachmentIndex = this.invoiceAttachments.indexOf(
                    this.attachmentToDelete
                )
                const origin =
                    attachmentIndex >= 0
                        ? this.attachmentOrigins[attachmentIndex]
                        : null
                if (attachmentIndex >= 0) {
                    this.invoiceAttachments.splice(attachmentIndex, 1)
                    this.attachmentOrigins.splice(attachmentIndex, 1)
                }

                // Check if attachment was in attachmentsToAdd (newly added)
                const attachmentsToAddIndex = this.attachmentsToAdd.indexOf(
                    origin == null ? this.attachmentToDelete : origin
                )
                if (attachmentsToAddIndex !== -1) {
                    // Attachment was newly added, just remove from attachmentsToAdd and pendingAttachmentsToUpload
                    this.attachmentsToAdd.splice(attachmentsToAddIndex, 1)

                    // Also remove from pendingAttachmentsToUpload (File objects)
                    this.pendingAttachmentsToUpload = this.pendingAttachmentsToUpload.filter(
                        file => file.name !== this.attachmentToDelete
                    )
                } else if (
                    origin &&
                    !this.attachmentsToRemove.includes(origin)
                ) {
                    this.attachmentsToRemove.push(origin)
                }

                this.deleteAttachmentDialog = false
                this.attachmentToDelete = null
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.deletingAttachment = null
            }
        },
        /**
         * Storage-safe unique names in the same folder; registers in `usedNames`.
         */
        uniqueFileName(originalName, usedNames) {
            const lastDot = originalName.lastIndexOf('.')
            const extension =
                lastDot !== -1 ? originalName.substring(lastDot) : ''
            const base =
                lastDot !== -1
                    ? originalName.substring(0, lastDot)
                    : originalName

            for (let n = 0; n < 10000; n += 1) {
                const candidate =
                    n === 0
                        ? `${base}${extension}`
                        : `${base} (${n})${extension}`
                if (!usedNames.has(candidate)) {
                    usedNames.add(candidate)
                    return candidate
                }
            }
            const fallback = `${base} (${Date.now()})${extension}`
            usedNames.add(fallback)
            return fallback
        },
        attachmentRenames() {
            const renames = []
            this.invoiceAttachments.forEach((name, index) => {
                const origin = this.attachmentOrigins[index]
                const nextName = (name || '').trim()
                if (origin && nextName && origin !== nextName) {
                    renames.push({ from: origin, to: nextName })
                }
            })
            return renames
        },
        async applyStorageRenames(renames) {
            const applied = []
            try {
                for (const rename of renames) {
                    await renameFile(rename.from, rename.to, rename.path)
                    applied.push(rename)
                }
            } catch (error) {
                for (const rename of applied.reverse()) {
                    try {
                        await renameFile(rename.to, rename.from, rename.path)
                    } catch (rollbackError) {
                        console.error(
                            `Error restoring ${rename.to}:`,
                            rollbackError
                        )
                    }
                }
                throw error
            }
            return applied
        },
        async updatePurchaseInvoice() {
            try {
                this.loading = true

                const originalFileName = this.originalFileName
                let fileRenames = []

                if (this.canEditInvoice) {
                    this.$set(
                        this.localInvoice,
                        'invoicesDate',
                        this.issueDateText
                    )
                    this.$set(
                        this.localInvoice,
                        'invoiceDate',
                        this.issueDateText
                    )
                    this.$set(this.localInvoice, 'dueDate', this.dueDateText)
                }

                if (this.selectedFile?.name && this.canRejectInvoice) {
                    // update the current file name based on the new file
                    this.localInvoice.fileName = this.selectedFile.name

                    // save the original file name to the past files array
                    if (!this.localInvoice.pastFiles) {
                        this.localInvoice.pastFiles = []
                    }
                    this.localInvoice.pastFiles.push(originalFileName)
                } else if (
                    this.canEditInvoice &&
                    this.fileNameDraft &&
                    this.fileNameDraft.trim() !== originalFileName
                ) {
                    const usedNames = this.collectUsedInvoiceFileNamesFromPurchase(
                        this.purchase
                    )
                    usedNames.delete(originalFileName)
                    const nextName = this.uniqueFileName(
                        this.fileNameDraft.trim(),
                        usedNames
                    )
                    this.fileNameDraft = nextName
                    this.localInvoice.fileName = nextName
                    fileRenames = [{ from: originalFileName, to: nextName }]
                }

                this.invoiceAttachments = this.invoiceAttachments
                    .map(name => (name || '').trim())
                    .filter(Boolean)
                const attachmentRenames = this.canEditInvoice
                    ? this.attachmentRenames()
                    : []
                this.localInvoice.invoiceAttachments = [
                    ...new Set(this.invoiceAttachments),
                ]

                const invoicePath = `${this.companyId}/purchases/${this.purchase.id}/invoices`
                const attachmentsPath = `${this.companyId}/purchases/${this.purchase.id}/invoiceAttatchments`
                const storedRenames = [
                    ...fileRenames.map(rename => ({
                        ...rename,
                        path: invoicePath,
                    })),
                    ...attachmentRenames
                        .filter(
                            rename =>
                                !this.attachmentsToRemove.includes(rename.from)
                        )
                        .map(rename => ({
                            ...rename,
                            path: attachmentsPath,
                        })),
                ]
                const appliedRenames = await this.applyStorageRenames(
                    storedRenames
                )
                try {
                    await API.updatePurchaseInvoice({
                        companyId: this.companyId,
                        purchaseId: this.purchase.id,
                        originalFileName: originalFileName,
                        invoice: this.localInvoice,
                        fileRenames,
                        attachmentRenames,
                    })
                } catch (error) {
                    await this.applyStorageRenames(
                        appliedRenames
                            .slice()
                            .reverse()
                            .map(rename => ({
                                from: rename.to,
                                to: rename.from,
                                path: rename.path,
                            }))
                    )
                    throw error
                }

                // Delete removed attachments BEFORE uploading new ones (same filename reuse)
                if (this.attachmentsToRemove.length > 0) {
                    for (const fileName of this.attachmentsToRemove) {
                        try {
                            await deleteFile(fileName, attachmentsPath)
                        } catch (error) {
                            console.error(
                                `Error deleting attachment ${fileName}:`,
                                error
                            )
                        }
                    }
                }

                if (this.pendingAttachmentsToUpload.length > 0) {
                    for (const file of this.pendingAttachmentsToUpload) {
                        try {
                            await saveFile(file, attachmentsPath, file.name)
                        } catch (error) {
                            console.error(
                                `Error uploading attachment ${file.name}:`,
                                error
                            )
                        }
                    }
                }

                // Main invoice file (different path than attachments)
                if (this.selectedFile?.name) {
                    await saveFile(
                        this.selectedFile,
                        `${this.companyId}/purchases/${this.purchase.id}/invoices`,
                        this.selectedFile.name
                    )
                }

                // Reset attachment tracking arrays
                this.pendingAttachmentsToUpload = []
                this.attachmentsToAdd = []
                this.attachmentsToRemove = []
                this.closeDialog()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
    },
}
</script>
